import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import type {
  GridColumnVisibilityModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import {
  hasPublicSearchUrlState,
  readPublicSearchUrlPage,
} from '@/lib/publicSearchPaths';

// Single source of truth for the default page/size, reused by the initial
// state, `resetPagination`, and the URL-deferred `merge` reset so they can
// never drift apart.
const DEFAULT_PAGE_SIZE = 50;
const DEFAULT_PAGINATION_MODEL = { page: 0, pageSize: DEFAULT_PAGE_SIZE };

type GridStore = {
  paginationModel: {
    page: number;
    pageSize: number;
  };
  scrollPosition: number;
  /** The list `scrollPosition` was kept for (see useGridScrollRestore). */
  scrollKey: string | null;
  gridSortModel: GridSortModel;
  columnVisibilityModel: GridColumnVisibilityModel;
  columnOrder: string[];
  manualColumnView: boolean;
  myAnimals: boolean;
  /** Animal search only: restrict to animals with at least one active sale / PT listing (`saleCount` &gt; 0). */
  animalsOnSaleOnly: boolean;
  showEBVs: boolean;
  showEBVAccuracy: boolean;
  showPercentiles: boolean;
  ebvCategoryVisibility: Record<string, boolean>;
  /** Per-EBV-column (per-trait) show/hide intent, keyed by grid field (e.g. EBV5). Persists independently of the EBV display toggles so toggling EBVs off/on doesn't lose per-trait choices. */
  ebvColumnVisibility: Record<string, boolean>;
  setMyAnimals: (show: boolean) => void;
  setAnimalsOnSaleOnly: (show: boolean) => void;
  setManualColumnView: (manual: boolean) => void;
  setColumnVisibilityModel: (model: GridColumnVisibilityModel) => void;
  setColumnOrder: (order: string[]) => void;
  setGridSortModel: (model: GridSortModel) => void;
  setPaginationModel: (model: { page: number; pageSize: number }) => void;
  setScrollPosition: (position: number) => void;
  /** Start keeping another list's scroll position, from `position`. */
  resetScrollPosition: (key: string, position: number) => void;
  setShowEBVs: (show: boolean) => void;
  setShowEBVAccuracy: (show: boolean) => void;
  setShowPercentiles: (show: boolean) => void;
  setEbvCategoryVisibility: (
    updater:
      | Record<string, boolean>
      | ((prev: Record<string, boolean>) => Record<string, boolean>),
  ) => void;
  setEbvColumnVisibility: (
    updater:
      | Record<string, boolean>
      | ((prev: Record<string, boolean>) => Record<string, boolean>),
  ) => void;
  resetPagination: () => void;
  resetColumnOrder: () => void;
};

/**
 * True when this document was loaded by a Back/Forward traversal rather than a
 * fresh visit. Such a load is a return to somewhere the user already was, so
 * the persisted query state still applies even when the URL arrives without
 * `?search=` — Next rebuilds the URL from its own router state and can drop a
 * param written with history.replaceState (BLS-396).
 */
function isHistoryTraversalLoad(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const [navigation] = performance.getEntriesByType(
      'navigation',
    ) as PerformanceNavigationTiming[];
    return navigation?.type === 'back_forward';
  } catch {
    return false;
  }
}

const useGridStore = create<GridStore>()(
  persist(
    devtools(
      immer((set) => ({
        paginationModel: { ...DEFAULT_PAGINATION_MODEL },
        scrollPosition: 0,
        scrollKey: null,
        gridSortModel: [],
        columnVisibilityModel: {},
        columnOrder: [],
        manualColumnView: false,
        myAnimals: false,
        animalsOnSaleOnly: false,
        showEBVs: false,
        showEBVAccuracy: false,
        showPercentiles: false,
        ebvCategoryVisibility: {},
        ebvColumnVisibility: {},
        setMyAnimals: (show) =>
          set((_state) => ({
            myAnimals: show,
          })),
        setAnimalsOnSaleOnly: (show) =>
          set((_state) => ({
            animalsOnSaleOnly: show,
          })),
        setManualColumnView: (manual) =>
          set((_state) => ({
            manualColumnView: manual,
          })),
        // Turning EBVs on brings Accuracy and Percentiles with it (users hide them
        // individually afterwards if they want); turning EBVs off hides all three.
        // Society capability gating (setting 26 showAccuracy / percentile bands)
        // is applied downstream via the effective* flags, so it is safe to set
        // both here even for values-only societies (BLS-341 / ILRX-4608).
        setShowEBVs: (show) =>
          set((_state) => ({
            showEBVs: show,
            showEBVAccuracy: show,
            showPercentiles: show,
          })),
        setShowEBVAccuracy: (show) =>
          set((_state) => ({
            showEBVAccuracy: show,
          })),
        setShowPercentiles: (show) =>
          set((_state) => ({
            showPercentiles: show,
          })),
        setEbvCategoryVisibility: (updater) =>
          set((_state) => ({
            ebvCategoryVisibility:
              typeof updater === 'function'
                ? updater(_state.ebvCategoryVisibility)
                : updater,
          })),
        setEbvColumnVisibility: (updater) =>
          set((_state) => ({
            ebvColumnVisibility:
              typeof updater === 'function'
                ? updater(_state.ebvColumnVisibility)
                : updater,
          })),
        setColumnVisibilityModel: (model) =>
          set((_state) => ({
            columnVisibilityModel: model,
          })),
        setColumnOrder: (order) =>
          set((_state) => ({
            columnOrder: order,
          })),
        setGridSortModel: (model) =>
          set((_state) => ({
            gridSortModel: model,
          })),
        setPaginationModel: (model) =>
          set((_state) => ({
            paginationModel: model,
          })),
        setScrollPosition: (position) =>
          set((_state) => ({
            scrollPosition: position,
          })),
        resetScrollPosition: (key, position) =>
          set((_state) => ({
            scrollKey: key,
            scrollPosition: position,
          })),
        resetPagination: () =>
          set((_state) => ({
            paginationModel: { ...DEFAULT_PAGINATION_MODEL },
          })),
        resetColumnOrder: () =>
          set((_state) => ({
            columnOrder: [],
          })),
      })),
      { name: 'GridStore' },
    ),
    {
      name: 'GridStore',
      storage: createJSONStorage(() => sessionStorage),
      // Defer to the URL: without `?search=` state, reset the query-driving
      // fields (sort, myAnimals, pagination) so a fresh visit starts empty.
      // Column/EBV display prefs are kept — they're UI preferences, not part
      // of the query. Mirrors usePublicSearchUrlSync's empty reset. See
      // useSearchStore for the full rationale.
      merge: (persisted, current) => {
        const merged = { ...current, ...(persisted as Partial<GridStore>) };
        if (hasPublicSearchUrlState()) {
          // BLS-396: the URL owns the page, so adopt it here rather than after
          // the first render — otherwise a load that lands on page N fetches
          // page 1 first and corrects itself once the URL sync runs. Page size
          // isn't serialized, so it stays whatever the session had.
          return {
            ...merged,
            paginationModel: {
              ...merged.paginationModel,
              page: readPublicSearchUrlPage(),
            },
          };
        }
        if (isHistoryTraversalLoad()) return merged;
        return {
          ...merged,
          gridSortModel: [],
          myAnimals: false,
          animalsOnSaleOnly: false,
          paginationModel: { ...DEFAULT_PAGINATION_MODEL },
        };
      },
    },
  ),
);

export default useGridStore;
